/**
 * Inbox page — list of threads grouped by counterparty.
 *
 * Renders:
 *   - <InboxHeader> with wallet monogram + sign-out menu
 *   - <InboxSkeleton> while loading
 *   - <InboxEmptyState> when there's nothing
 *   - flat <ul> of <InboxRow> otherwise
 */
import { useInbox } from "../hooks/useInbox";
import { useAuth } from "../hooks/useAuth";
import { InboxHeader } from "../components/InboxHeader";
import { InboxRow } from "../components/InboxRow";
import { InboxEmptyState } from "../components/EmptyState";
import { InboxSkeleton } from "../components/Skeleton";

export function InboxPage(): JSX.Element {
  const { status } = useAuth();
  const { data, isLoading, isError, refetch } = useInbox(status === "ready");

  return (
    <div className="flex h-full flex-col bg-tg-bg">
      <InboxHeader />
      <main className="scroll-area flex-1 overflow-y-auto">
        {isLoading ? (
          <InboxSkeleton />
        ) : isError ? (
          <div className="flex h-full flex-col items-center justify-center gap-3 px-8 text-center">
            <p className="text-sm text-tg-destructive">
              Could not load your inbox.
            </p>
            <button
              onClick={() => refetch()}
              className="rounded-md bg-tg-button px-4 py-2 text-sm font-medium text-tg-button-text"
            >
              Retry
            </button>
          </div>
        ) : !data || data.threads.length === 0 ? (
          <InboxEmptyState />
        ) : (
          <ul className="divide-y divide-tg-separator" role="list">
            {data.threads.map((t) => (
              <InboxRow key={t.counterparty_raw} thread={t} />
            ))}
          </ul>
        )}
      </main>
    </div>
  );
}
